import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useMenu } from '../../hooks/useMenu';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import LogoBlock from './LogoBlock';
import { Button } from '../ui/button';
import { NotificationBell } from '../notifications/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Icon } from '../common/Icon';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { Ltr } from '../common/Bidi';
import { useDirection } from '../../i18n/direction';
import { getInitials } from '../../lib/utils';

const isExternalHref = (href) => typeof href === 'string' && /^https?:\/\//i.test(href);

/**
 * Render one header menu node on desktop. If it has children, we show a
 * hover/click dropdown; otherwise a plain link.
 */
function DesktopNavNode({ item, pathname, onNavigate }) {
  const baseCls = `text-sm font-medium brand-header-link${
    pathname === item.href ? ' font-semibold' : ''
  }`;

  if (item.children && item.children.length > 0) {
    return (
      <DropdownMenu key={item.id}>
        <DropdownMenuTrigger asChild>
          <button
            className={`${baseCls} inline-flex items-center gap-1`}
            data-testid={`nav-${item.id}`}
          >
            {item.label}
            <Icon name="chevron-down" className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[12rem]">
          {item.children.map((child) => {
            const childHref = child.href || '/';
            return (
              <DropdownMenuItem key={child.id} asChild>
                {isExternalHref(childHref) ? (
                  <a
                    href={childHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onNavigate}
                    data-testid={`nav-${child.id}`}
                  >
                    {child.label}
                    <Icon name="external-link" className="h-3 w-3 ms-1 inline" />
                  </a>
                ) : (
                  <Link to={childHref} onClick={onNavigate} data-testid={`nav-${child.id}`}>
                    {child.label}
                  </Link>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <NavLink
      key={item.id}
      item={item}
      className={baseCls}
      onNavigate={onNavigate}
      testId={`nav-${item.id}`}
    />
  );
}

function NavLink({ item, className = '', onNavigate, testId }) {
  const href = item.href || '/';
  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onNavigate}
        data-testid={testId}
      >
        {item.label}
        <Icon name="external-link" className="h-3 w-3 ms-1 inline" />
      </a>
    );
  }
  return (
    <Link to={href} className={className} onClick={onNavigate} data-testid={testId}>
      {item.label}
    </Link>
  );
}

/**
 * Mobile accordion row. If the item has children, clicking it toggles
 * an expansion; otherwise it navigates immediately.
 */
function MobileNavNode({ item, pathname, onNavigate, depth = 0 }) {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const baseCls = `flex items-center justify-between gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-colors hover:bg-muted${
    pathname === item.href ? ' bg-muted text-foreground' : ' text-muted-foreground'
  }`;

  const indent = { paddingLeft: 12 + depth * 16 };

  if (hasChildren) {
    return (
      <div>
        <button
          className={`${baseCls} w-full text-start`}
          style={indent}
          onClick={() => setOpen((v) => !v)}
          data-testid={`mobile-nav-${item.id}`}
        >
          <span>{item.label}</span>
          <Icon name="chevron-down" className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="space-y-1" data-testid={`mobile-nav-${item.id}-children`}>
            {item.children.map((child) => (
              <MobileNavNode
                key={child.id}
                item={child}
                pathname={pathname}
                onNavigate={onNavigate}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const href = item.href || '/';
  const common = {
    className: baseCls,
    style: indent,
    onClick: onNavigate,
    'data-testid': `mobile-nav-${item.id}`,
  };
  if (isExternalHref(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...common}>
        <span>{item.label}</span>
        <Icon name="external-link" className="h-3 w-3" />
      </a>
    );
  }
  return (
    <Link to={href} {...common}>
      <span>{item.label}</span>
    </Link>
  );
}

export function Header() {
  const { user, isAuthenticated, logout, isCustomer, isGarage, isDealer, isAdmin } = useAuth();
  const { header_background_color } = useSiteSettings();
  const { isRtl } = useDirection();
  const { t } = useTranslation('shared');
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showGetStartedModal, setShowGetStartedModal] = useState(false);
  const drawerRef = useRef(null);

  // Data-driven menus (Menu Manager → /api/menus/{type})
  const { items: headerMenu } = useMenu('header');
  const { items: mobileMenu } = useMenu('mobile');

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Allow CMS-authored Get Started CTAs to open the same role-selection
  // dialog used by the header button. PublicPage dispatches this browser event
  // after safely intercepting a marked CMS link.
  useEffect(() => {
    const openGetStartedModal = () => {
      setShowGetStartedModal(true);
    };

    window.addEventListener('snappipit:open-get-started', openGetStartedModal);

    return () => {
      window.removeEventListener('snappipit:open-get-started', openGetStartedModal);
    };
  }, []);


  // Scroll lock when mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close on outside click
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [mobileOpen]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    if (isDealer) return '/dealer/dashboard';
    if (isGarage) return '/garage/dashboard';
    return '/dashboard';
  };

  const handleGetStartedClick = () => setShowGetStartedModal(true);
  const handleSelectRepair = () => { setShowGetStartedModal(false); navigate('/repair/start'); };
  const handleSelectParts = () => { setShowGetStartedModal(false); navigate('/parts/start'); };

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full border-b ${isAuthenticated && !isAdmin ? 'auth-topbar border-slate-200 bg-white/95 backdrop-blur' : (header_background_color ? '' : 'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60')}`}
        style={!isAuthenticated && header_background_color ? { backgroundColor: header_background_color } : undefined}
        data-testid="public-header"
      >
        <div className={`${isAuthenticated && !isAdmin ? 'max-w-none px-4 sm:px-6 lg:px-8' : 'container mx-auto px-4'} flex ${isAuthenticated ? 'h-16' : 'h-20 sm:h-24'} items-center justify-between`}>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              aria-label={mobileOpen ? t('menu.close') : t('menu.open')}
            >
              {mobileOpen ? <Icon name="x" className="h-5 w-5" /> : <Icon name="menu" className="h-5 w-5" />}
            </Button>

            <LogoBlock variant={isAuthenticated ? 'default' : 'public'} to="/" testId="header-logo" />
            {isAuthenticated && !isAdmin && (
              <div className="hidden sm:block border-s border-slate-200 ps-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{t('workspace.authenticated')}</p>
                <p className="text-sm font-semibold text-slate-700">{isDealer ? t('workspace.dealer') : isGarage ? t('workspace.garage') : t('workspace.vehicleOwner')}</p>
              </div>
            )}
          </div>

          {/* Desktop Nav — driven by Menu Manager 'header' */}
          <nav className={`${isAuthenticated ? 'hidden' : 'hidden md:flex'} items-center gap-6`} data-testid="desktop-nav">
            {headerMenu.map((item) => (
              <DesktopNavNode
                key={item.id}
                item={item}
                pathname={location.pathname}
                onNavigate={closeMobile}
              />
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher className="hidden sm:inline-flex" />
            {isAuthenticated ? (
              <>
                <NotificationBell />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-trigger">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.avatar_url} alt={user?.name} />
                      <AvatarFallback className="bg-accent text-accent-foreground">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user?.name}</p>
                      <p className="text-xs leading-none text-muted-foreground"><Ltr>{user?.phone}</Ltr></p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="cursor-pointer" data-testid="menu-dashboard">
                      <Icon name="layout-dashboard" className="me-2 h-4 w-4" />{t('sidebar.dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  {isCustomer && (
                    <DropdownMenuItem asChild>
                      <Link to="/vehicles" className="cursor-pointer" data-testid="menu-vehicles">
                        <Icon name="car" className="me-2 h-4 w-4" />{t('sidebar.myVehicles')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isGarage && (
                    <DropdownMenuItem asChild>
                      <Link to="/garage/profile" className="cursor-pointer" data-testid="menu-garage-profile">
                        <Icon name="wrench" className="me-2 h-4 w-4" />{t('providerProfile.title.garage')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isDealer && (
                    <DropdownMenuItem asChild>
                      <Link to="/dealer/profile" className="cursor-pointer" data-testid="menu-dealer-profile">
                        <Icon name="store" className="me-2 h-4 w-4" />{t('providerProfile.title.dealer')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {/* Generic /profile is customer-only on the backend. Hide it
                      for providers (garage/dealer) so they don't get bounced
                      back to their dashboard by ProtectedRoute. They already
                      have role-specific profile entries above. */}
                  {!isGarage && !isDealer && (
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer" data-testid="menu-profile">
                        <Icon name="user" className="me-2 h-4 w-4" />{t('sidebar.profile')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isCustomer && (
                    <DropdownMenuItem asChild>
                      <Link to="/support/tickets" className="cursor-pointer" data-testid="menu-my-tickets">
                        <Icon name="life-buoy" className="me-2 h-4 w-4" />{t('support.myTickets')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer text-destructive" onClick={logout} data-testid="menu-logout">
                    <Icon name="log-out" className="me-2 h-4 w-4" />{t('actions.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" asChild className="hidden sm:inline-flex" data-testid="header-login">
                  <Link to="/login">{t('actions.signUp')}</Link>
                </Button>
                <Button className="brand-cta" onClick={handleGetStartedClick} data-testid="header-get-started">
                  {t('actions.getStarted')}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" data-testid="mobile-overlay" />
      )}

      {/* Mobile Menu Drawer */}
      <div
        ref={drawerRef}
        className={`fixed top-0 start-0 z-50 h-full w-72 bg-background border-e shadow-xl transform transition-transform duration-300 ease-in-out md:hidden ${mobileOpen ? 'translate-x-0' : (isRtl ? 'translate-x-full' : '-translate-x-full')}`}
        data-testid="mobile-menu-drawer"
      >
        <div className="flex items-center justify-between p-4 border-b">
          <LogoBlock variant="drawer" to="/" onClick={closeMobile} testId="mobile-drawer-logo" />
          <Button variant="ghost" size="icon" onClick={closeMobile} data-testid="mobile-menu-close">
            <Icon name="x" className="h-5 w-5" />
          </Button>
        </div>

        {/* Mobile Nav — driven by Menu Manager 'mobile' (falls back to 'header' if mobile menu not configured) */}
        <nav className="p-4 space-y-1 overflow-y-auto" data-testid="mobile-nav" style={{ maxHeight: 'calc(100% - 180px)' }}>
          {(mobileMenu.length > 0 ? mobileMenu : headerMenu).map((item) => (
            <MobileNavNode
              key={item.id}
              item={item}
              pathname={location.pathname}
              onNavigate={closeMobile}
            />
          ))}
        </nav>

        <div className="absolute bottom-0 start-0 end-0 p-4 border-t bg-background">
          <div className="mb-2 sm:hidden"><LanguageSwitcher /></div>
          {isAuthenticated ? (
            <div className="space-y-2">
              <Link to={getDashboardLink()} onClick={closeMobile} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium hover:bg-muted">
                <Icon name="layout-dashboard" className="h-4 w-4" />{t('sidebar.dashboard')}
              </Link>
              <button onClick={() => { logout(); closeMobile(); }} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 w-full text-start">
                <Icon name="log-out" className="h-4 w-4" />{t('actions.logout')}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <Button className="w-full brand-cta" onClick={() => { closeMobile(); handleGetStartedClick(); }}>
                {t('actions.getStarted')}
              </Button>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/login" onClick={closeMobile}>{t('actions.signUpLogin')}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Get Started Modal */}
      <Dialog open={showGetStartedModal} onOpenChange={setShowGetStartedModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">{t('roleDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Customer paths */}
            <button
              onClick={handleSelectRepair}
              className="flex items-center gap-4 p-4 rounded-lg border-2 border-border hover:border-accent hover:bg-accent/5 transition-all group"
              data-testid="modal-repair-option"
            >
              <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center group-hover:bg-accent/25 transition-colors">
                <Icon name="wrench" className="h-6 w-6 text-accent" />
              </div>
              <div className="flex-1 text-start">
                <h3 className="font-semibold text-foreground">{t('roleDialog.repairTitle')}</h3>
                <p className="text-sm text-muted-foreground">{t('roleDialog.repairBody')}</p>
              </div>
              <Icon name="arrow-right" className="h-5 w-5 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all" />
            </button>

            <button
              onClick={handleSelectParts}
              className="flex items-center gap-4 p-4 rounded-lg border-2 border-border hover:border-primary hover:bg-primary/5 transition-all group"
              data-testid="modal-parts-option"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Icon name="package" className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 text-start">
                <h3 className="font-semibold text-foreground">{t('roleDialog.partsTitle')}</h3>
                <p className="text-sm text-muted-foreground">{t('roleDialog.partsBody')}</p>
              </div>
              <Icon name="arrow-right" className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
            </button>

            {/* Divider */}
            <div className="relative my-1" aria-hidden="true">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground tracking-wider">{t('roleDialog.becomeProvider')}</span>
              </div>
            </div>

            {/* Provider signup paths */}
            <Link
              to="/login?register=true&role=garage_owner"
              onClick={() => setShowGetStartedModal(false)}
              className="flex items-center gap-4 p-4 rounded-lg border-2 border-border hover:border-amber-500 hover:bg-amber-50/40 dark:hover:bg-amber-950/20 transition-all group"
              data-testid="modal-provider-garage-option"
            >
              <div className="w-12 h-12 rounded-lg bg-amber-500/15 flex items-center justify-center group-hover:bg-amber-500/25 transition-colors">
                <Icon name="building-2" className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1 text-start">
                <h3 className="font-semibold text-foreground">{t('roleDialog.garageTitle')}</h3>
                <p className="text-sm text-muted-foreground">{t('roleDialog.garageBody')}</p>
              </div>
              <Icon name="arrow-right" className="h-5 w-5 text-muted-foreground group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
            </Link>

            <Link
              to="/login?register=true&role=dealer_owner"
              onClick={() => setShowGetStartedModal(false)}
              className="flex items-center gap-4 p-4 rounded-lg border-2 border-border hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 transition-all group"
              data-testid="modal-provider-dealer-option"
            >
              <div className="w-12 h-12 rounded-lg bg-emerald-500/15 flex items-center justify-center group-hover:bg-emerald-500/25 transition-colors">
                <Icon name="store" className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1 text-start">
                <h3 className="font-semibold text-foreground">{t('roleDialog.dealerTitle')}</h3>
                <p className="text-sm text-muted-foreground">{t('roleDialog.dealerBody')}</p>
              </div>
              <Icon name="arrow-right" className="h-5 w-5 text-muted-foreground group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Header;